/**
 * Utilitários para normalização de telefones brasileiros
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠️ CRÍTICO: Esta função deve ser usada em TODA comparação de telefones no sistema
 * para evitar duplicações causadas pelo 9º dígito de celulares brasileiros.
 * 
 * Problema: Z-API pode retornar números com ou sem o 9, e o banco pode ter
 * o formato oposto, causando duplicação de contatos.
 * 
 * Solução: Normalizar SEMPRE para formato COM 9 (13 dígitos) antes de comparar.
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Histórico:
 * - Dezembro/2024: Criado para resolver duplicação de contatos na importação
 */

/**
 * Normaliza número de celular brasileiro adicionando o 9º dígito se necessário
 * 
 * Padrão final: 55 + DDD(2) + 9 + número(8) = 13 dígitos
 * 
 * Exemplos:
 * - 556291986300 (12 dígitos) → 5562991986300 (13 dígitos)
 * - 5562991986300 (13 dígitos) → 5562991986300 (mantém)
 * - 11987654321 (11 dígitos) → 5511987654321 (adiciona 55)
 * - +55 62 9 9198-6300 → 5562991986300 (limpa e normaliza)
 * 
 * @param phone Telefone em qualquer formato
 * @returns Telefone normalizado (apenas dígitos, 13 dígitos para celular BR)
 */
export function normalizeBrazilianPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Remove tudo que não é dígito
  const digits = phone.replace(/\D/g, '');
  
  // Se vazio, retorna vazio
  if (!digits) return '';
  
  // Se for número brasileiro com 12 dígitos (formato antigo sem 9)
  // Ex: 556293252289 -> 5562993252289
  if (/^55\d{10}$/.test(digits)) {
    const countryCode = '55';
    const areaCode = digits.substring(2, 4);
    const localNumber = digits.substring(4);
    
    // Se começa com 6, 7, 8 ou 9, é celular - adiciona o 9
    // (números de celular no Brasil começam com 6, 7, 8 ou 9 após o DDD)
    if (/^[6789]/.test(localNumber)) {
      return countryCode + areaCode + '9' + localNumber;
    }
  }
  
  // Retorna apenas os dígitos (pode ser fixo ou formato já correto)
  return digits;
}

/**
 * Compara dois telefones considerando normalização brasileira
 * 
 * ⚠️ USE ESTA FUNÇÃO para comparar telefones em vez de comparação direta!
 * 
 * @param phone1 Primeiro telefone
 * @param phone2 Segundo telefone
 * @returns true se são o mesmo número (ignorando diferenças de 9º dígito)
 */
export function isSamePhone(phone1: string | null | undefined, phone2: string | null | undefined): boolean {
  const normalized1 = normalizeBrazilianPhone(phone1);
  const normalized2 = normalizeBrazilianPhone(phone2);
  
  if (!normalized1 || !normalized2) return false;
  
  return normalized1 === normalized2;
}

/**
 * Cria um Set de telefones normalizados para comparação eficiente
 * 
 * Use esta função para criar um Set otimizado para verificar existência
 * de telefones em uma lista grande.
 * 
 * Exemplo:
 * ```typescript
 * const existingSet = createNormalizedPhoneSet(existingPhones);
 * const isNew = !existingSet.has(normalizeBrazilianPhone(newPhone));
 * ```
 * 
 * @param phones Array de telefones
 * @returns Set com todos os telefones normalizados
 */
export function createNormalizedPhoneSet(phones: (string | null | undefined)[]): Set<string> {
  return new Set(
    phones
      .map(p => normalizeBrazilianPhone(p))
      .filter(p => p.length > 0)
  );
}

/**
 * Verifica se um telefone já existe em uma lista, usando normalização brasileira
 * 
 * @param phone Telefone a verificar
 * @param existingPhones Lista de telefones existentes
 * @returns true se o telefone já existe na lista
 */
export function phoneExistsInList(
  phone: string | null | undefined, 
  existingPhones: (string | null | undefined)[]
): boolean {
  const normalizedPhone = normalizeBrazilianPhone(phone);
  if (!normalizedPhone) return false;
  
  return existingPhones.some(existing => 
    normalizeBrazilianPhone(existing) === normalizedPhone
  );
}

/**
 * Formata número de telefone para exibição com máscara
 * 
 * Exemplos:
 * - 5562985585466 → +55 62 98558-5466 (celular 13 dígitos)
 * - 556233334444 → +55 62 3333-4444 (fixo 12 dígitos)
 * - 11987654321 → +55 11 98765-4321 (11 dígitos sem código de país)
 * 
 * @param phone Telefone em qualquer formato
 * @returns Telefone formatado para exibição
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return '';
  
  const digits = phone.replace(/\D/g, '');
  
  // Formato brasileiro com 13 dígitos (celular): +55 XX XXXXX-XXXX
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  
  // Formato brasileiro com 12 dígitos (fixo): +55 XX XXXX-XXXX
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  
  // Formato brasileiro sem código de país (11 dígitos celular)
  if (digits.length === 11) {
    return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  
  // Formato brasileiro sem código de país (10 dígitos fixo)
  if (digits.length === 10) {
    return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  
  // Se já tem +, mantém como está
  if (phone.startsWith('+')) return phone;
  
  // Fallback: retorna com + no início
  return digits ? `+${digits}` : '';
}

/**
 * Verifica se é um número especial brasileiro (0800, 4003, 400X, 300X)
 * Números especiais não recebem código de país
 * 
 * @param digits Número apenas com dígitos
 * @returns true se for número especial
 */
export function isSpecialPhoneNumber(digits: string): boolean {
  if (!digits) return false;
  return /^(0800|0300|400[0-9]|300[0-9])/.test(digits);
}

/**
 * Normaliza identificador de telefone para armazenamento padronizado
 * 
 * Regras:
 * - Remove todos os caracteres não numéricos
 * - Números especiais (0800, 4003, etc): mantém sem código de país
 * - Números BR sem DDI (10-11 dígitos): adiciona prefixo 55
 * - Números BR 55 + 10 dígitos (celular sem 9): adiciona o 9º dígito
 * - Números internacionais: mantém como está
 * 
 * @param identifier Identificador em qualquer formato
 * @returns Identificador normalizado (apenas dígitos)
 */
export function normalizePhoneIdentifier(identifier: string | null | undefined): string {
  if (!identifier) return '';
  
  // Remove tudo que não é dígito
  const digits = identifier.replace(/\D/g, '');
  
  if (!digits) return '';
  
  // Números especiais (0800, 4003, 400X, 300X) - mantém sem código de país
  if (isSpecialPhoneNumber(digits)) {
    return digits;
  }
  
  // Se já tem DDI brasileiro (55) e 12 dígitos, normaliza adicionando 9º dígito se for celular
  if (digits.length === 12 && digits.startsWith('55')) {
    const areaCode = digits.substring(2, 4);
    const localNumber = digits.substring(4);
    
    // Se começa com 6, 7, 8 ou 9, é celular - adiciona o 9
    if (/^[6789]/.test(localNumber)) {
      return '55' + areaCode + '9' + localNumber;
    }
    return digits;
  }
  
  // Se já tem DDI brasileiro (55) e 13 dígitos, já está correto
  if (digits.length === 13 && digits.startsWith('55')) {
    return digits;
  }
  
  // Número BR sem DDI (11 dígitos = celular com 9)
  if (digits.length === 11) {
    return '55' + digits;
  }
  
  // Número BR sem DDI (10 dígitos = fixo ou celular antigo)
  if (digits.length === 10) {
    const areaCode = digits.substring(0, 2);
    const localNumber = digits.substring(2);
    
    // Se começa com 6, 7, 8 ou 9, é celular sem o 9 - adiciona
    if (/^[6789]/.test(localNumber)) {
      return '55' + areaCode + '9' + localNumber;
    }
    // Fixo
    return '55' + digits;
  }
  
  // Número internacional ou outro formato - mantém como está
  return digits;
}

/**
 * Formata identificador de inbox para exibição na UI
 * Aplica máscara apropriada baseada no tipo de canal
 * 
 * @param channelType Tipo do canal (whatsapp, telegram, instagram, etc)
 * @param identifier Identificador da inbox
 * @returns Identificador formatado para exibição
 */
export function formatInboxIdentifierForDisplay(
  channelType: string | null | undefined, 
  identifier: string | null | undefined
): string {
  if (!identifier) return '';
  
  // Apenas canais de telefone são formatados
  const isPhoneChannel = channelType === 'whatsapp' || channelType === 'telegram' || channelType === 'whatsapp_oficial';
  
  if (!isPhoneChannel) {
    return identifier;
  }
  
  const digits = identifier.replace(/\D/g, '');
  
  // Números especiais - formata com máscara especial
  if (isSpecialPhoneNumber(digits)) {
    // 0800: 0800-XXX-XXXX
    if (digits.startsWith('0800') && digits.length >= 11) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    // 4003/400X: 4003-XXXX ou 400X-XXXX
    if (/^400[0-9]/.test(digits) && digits.length >= 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    // 300X: 300X-XXXX
    if (/^300[0-9]/.test(digits) && digits.length >= 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    // 0300: 0300-XXX-XXXX
    if (digits.startsWith('0300') && digits.length >= 11) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    // Fallback para especiais
    return identifier;
  }
  
  // Números normais - usa formatação padrão de telefone
  return formatPhoneForDisplay(digits);
}
